import Vue from "vue";
import * as storeHelper from "@/shared/storeHelper";
import * as expenseService from "@/shared/api/expenseService";

import {
    FetchState,
    ExpenseReimburseState,
    UnreimburseExpense,
    UnreimbursedPage,
    ReimbursementGetResponse,
    ReimbursementExpensesGetResponse,
} from "@/shared/types";
import { defineStore } from "pinia";
import _ from "lodash";

const generateEmptyState = () => ({
    fetchState: FetchState.Uninitialized,
    transactionFetchState: FetchState.Uninitialized,
    page: 1,
    pageSize: 10,
    numberOfPages: 1,
    numberOfReimbursements: 0,
    records: [],
    expenseTransactionDictionary: {}
});

export const initialState: ExpenseReimburseState = generateEmptyState();

export const useExpenseReimbursedStore = defineStore("expenseReimbursed", {
    state: (): ExpenseReimburseState => (_.cloneDeep(initialState)),
    getters: {
        getUnreimbursedLineChart: state => [],
        getExpensesList: state => page => {
            if (page <= 0 || page > state.numberOfPages) {
                return [];
            }
            return state.records.slice((page - 1) * state.pageSize, page * state.pageSize);
        },
        hasPages: state => state.numberOfPages > 1,
        currentPage: state => state.page,
        pageCount: state => state.numberOfPages,
        pageRange: state => _.range(state.page - 2, state.page + 3)
            .filter(page => page > 0 && page <= state.numberOfPages),
        currentPageSelected () {
            return _.every(this.getExpensesList(this.page), expense => {
                return expense && expense.isSelected ? expense.isSelected : false;
            });
        },
        totalNumberOfReimbursements: state => state.numberOfReimbursements,
        selectedExpenses: state => _.filter(state.records, ex => ex && ex.isSelected),
        selectedExpenseIds () {
          return  _.map(<UnreimburseExpense[]>this.selectedExpenses, ex => ex.id);
        },
        selectedTotal () {
            return _.reduce(this.selectedExpenses, (sum, ex) => sum + ex.amount, 0);
        }
    },
    actions: {
        setFetchState (fetchState: FetchState) {
            this.fetchState = fetchState;
        },
        setFromResponse (response: ReimbursementGetResponse) {
            this.page = response.page;
            this.numberOfPages = response.numberOfPages;
            this.numberOfReimbursements = response.numberOfReimbursements;

            let sIndex = (this.page - 1) * this.pageSize;
            const rlen = response.records.length;
            for (let index = 0; index < rlen; index++, sIndex++) {
                response.records[index].childExpenseLoaded = false;
                Vue.set(this.records, sIndex, response.records[index]);
            }

            const slen = this.records.length;
            for (; sIndex < slen; sIndex++) {
                Vue.delete(this.records, sIndex);
            }

            this.fetchState = FetchState.Loaded;
        },
        setExpensesForReimbursement (data: ReimbursementExpensesGetResponse) {
            Vue.set(this.expenseTransactionDictionary, `${data.id}`, data.appliedExpenses);
            let foundIndex = 0;
            let copiedResult = null;
            this.records.forEach((item, index) => {
                if (item.id === data.id) {
                    foundIndex = index;
                    item.childExpenses = data.appliedExpenses;
                    item.childExpenseLoaded = true;
                    copiedResult = { ...item };
                }
            });
            Vue.set(this.records, foundIndex, copiedResult);
        },
        updateExpense (data: any) {
            this.records.forEach((item, recordIndex) => {
                if (item.childExpenses) {
                    const expenseIndex = item.childExpenses.findIndex(e => e.id === data.id);
                    if (expenseIndex >= 0) {
                        if (data.expenseEvidenceAttachments && data.expenseEvidenceAttachments.length) {
                            data.attachmentNames = data.attachmentNames.concat(
                                data.expenseEvidenceAttachments.map(d => d.name));
                        }
                        this.records[recordIndex].childExpenses.splice(expenseIndex, 1, data);
                    }
                }
            });
        },
        removeExpenseEvidence ({ attachmentName, id }: any) {
            this.records.forEach((item, recordIndex) => {
                if (item.childExpenses) {
                    const expenseIndex = item.childExpenses.findIndex(e => e.id === id);
                    if (expenseIndex >= 0) {
                        const expense = this.records[recordIndex].childExpenses[expenseIndex];
                        expense.attachmentNames = expense.attachmentNames.filter(name => name !== attachmentName);
                        this.records[recordIndex].childExpenses.splice(expenseIndex, 1, expense);
                    }
                }
            });
        },
        clear () {
            const emptyState = generateEmptyState();
            for (const v of Object.keys(emptyState)) {
                this[v] = emptyState[v];
            }
        },
        clearDictionary () {
            this.expenseTransactionDictionary = {};
        },
        load () {
            if (this.fetchState !== FetchState.Uninitialized) {
                return Promise.resolve();
            }

            this.setFetchState(FetchState.Loading);
            return expenseService.reimbursedGet(this.page, this.pageSize, true).then(response => {
                this.setFromResponse(response.data);
            }).catch(err => storeHelper.handleError({ error: err, rethrow: true }));
        },
        loadPage (request: UnreimbursedPage) {
            if (this.fetchState !== FetchState.Loaded || !request || request.page <= 0 || request.page >
                this.numberOfPages) {
                return Promise.resolve();
            }

            const elist = this.getExpensesList(request.page);
            const fullPage = _.every(elist, _.identity);
            if (elist.length && fullPage && !request.force) {
                this.page = request.page;
                return Promise.resolve();
            }

            return expenseService.reimbursedGet(request.page, this.pageSize, true).then(response => {
                this.setFromResponse(response.data);
            }).catch(err => storeHelper.handleError({ error: err, rethrow: true }));
        },
        loadExpensesForReimbursement (reimbursementId: number) {
            if (this.expenseTransactionDictionary && _.has(this.expenseTransactionDictionary, reimbursementId)) {
                return Promise.resolve();
            }
            return expenseService.reimbursedExpensesByReimbursementId(reimbursementId).then(response => {
                this.setExpensesForReimbursement(response.data);
            }).catch(err => storeHelper.handleError({ error: err, rethrow: true }));
        },
        reloadPage () {
            this.loadPage({
                page: this.page,
                force: true
            });
        },
    }
});